import React, { useCallback, useEffect, useMemo, useState } from "react";
import Fuse from "fuse.js";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import swal from '@sweetalert/with-react';
import "../../../../css/course_requests.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars, faPlusCircle, faSearch, faTimes, faTrashAlt
} from "@fortawesome/free-solid-svg-icons";

import Loader from "../../common/Loader";
import CourseIcon from "../../common/CourseIcon";
import Toggle from "../../../Toggle";
import SearchAddButton from "../../../SearchAddButton";
import { studentValidateRequests } from "../../../../js/Helpers";
import { buildCourseSectionLabelMap, buildLabelsById, getVisibleRequestLabels, sectionSatisfiesStudentLabelsForCourse } from "../../utils/studentLabelHelpers";
import { getLabelTextColorClass } from "../../utils/labelColorUtils";
import { getCourseIcon } from "../../utils/courseIcons";

const requestIsInclusion = (courseRequest = {}) => `${courseRequest.is_inclusion || '0'}` === '1';
const EMPTY_COURSE_ICON_LOOKUP = Object.freeze({});

CourseRequests.defaultProps = {
  highlightedRequests: []
};

export default function CourseRequests({
  schoolId = null,
  scheduleVersionId = null,
  scheduleInfo = {},
  studentRequests = [],
  courses,
  courseIconsByCourseID = EMPTY_COURSE_ICON_LOOKUP,
  highlightedRequests,
  onUpdateData = () => {},
  onUpdateStudentRequests = () => {},
  toggleRequestHighlight = () => {},
  editable = true,
  showHeader = true,
  showTotalCredits = true,
  studentCourseSections = [],
  sections = [],
  labels = [],
  studentLabels = [],
  onRequestRowClick = () => {},
  renderRequestExpandedContent = null,
  onCloseRequestExpandedContent = () => {},
}) {
  const [studentRequestsState, setStudentRequestsState] = useState([]);
  const [addStudentRequestOpen, setAddStudentRequestOpen] = useState(false);
  const [openedAlternate, setOpenedAlternate] = useState([]);
  const [studentRequestResults, setStudentRequestResults] = useState([]);
  const [validateRequestsLoading, setValidateRequestsLoading] = useState({
    isLoading: false,
    context: null,
    targetId: null
  });

  const coursesIndex = useMemo(
    () =>
      courses.reduce((index, course) => {
        index[course.course_id] = course;
        return index;
      }, {}),
    [courses]
  );

  const labelsByID = useMemo(
    () => buildLabelsById(labels),
    [labels]
  );

  const courseSectionLabelMap = useMemo(
    () => buildCourseSectionLabelMap(sections),
    [sections]
  );

  const getRequestLabels = useCallback((courseID) => {
    return getVisibleRequestLabels({
      student_labels: studentLabels,
      course_id: courseID,
      course_section_label_map: courseSectionLabelMap,
      labels_by_id: labelsByID,
    }).sort((labelA, labelB) => `${labelA.label}`.localeCompare(`${labelB.label}`));
  }, [studentLabels, courseSectionLabelMap, labelsByID]);

  const initialCourseRequests = useMemo(
    () =>
      studentRequests.map((sReq) => {
        const course_requests = [];
        let primaryCourseReq = null;
        let primaryCourse = null;

        if (sReq.course_requests && sReq.course_requests.length > 0) {
          for (let cr of sReq.course_requests) {
            const course = coursesIndex[cr.course_id] || {};

            if (Object.keys(course).length === 0 || !course.name || !course.course_code) continue;

            const courseIcon = getCourseIcon(courseIconsByCourseID, cr.course_id, {
              request_icon: cr.request_icon || (`${cr.order}` === '1' ? sReq.request_icon : null),
              color: cr.color || (`${cr.order}` === '1' ? sReq.color : null),
            });

            let newCourseRequest = {
              course_id: cr.course_id,
              name: `${course.name} (${course.course_code})`,
              credits: course.credits,
              order: cr.order,
              is_inclusion: cr.is_inclusion || '0',
              data_origin: cr.data_origin || '2',
              is_deleted: cr.is_deleted || '0',
              matched: cr.matched || '0',
              request_icon: cr.request_icon || (`${cr.order}` === '1' ? sReq.request_icon : null),
              course_icon_slot: courseIcon.icon_slot,
              color: courseIcon.color,
            };
            if (`${cr.order}` === "1") {
              primaryCourseReq = newCourseRequest;
              primaryCourse = course;
            }
            course_requests.push(newCourseRequest);
          }
        }

        // Fallback for legacy/request-shape edge cases where order may not be "1" exactly.
        if (!primaryCourseReq && course_requests.length > 0) {
          const fallbackCourseRequest = course_requests[0] || null;
          if (fallbackCourseRequest) {
            primaryCourseReq = fallbackCourseRequest;
            primaryCourse = coursesIndex[fallbackCourseRequest.course_id] || {};
          }
        }

        course_requests.sort((a, b) => parseInt(a.order) - parseInt(b.order));

        return {
          id: sReq.group_id,
          name: primaryCourse ? `${primaryCourse.name} (${primaryCourse.course_code})` : "",
          credits: primaryCourse ? primaryCourse.credits : null,
          priority: sReq.priority,
          course_requests: course_requests,
          isOpen: false,
          request_course_id: primaryCourseReq ? primaryCourseReq.course_id : null,
          request_icon: primaryCourseReq ? (primaryCourseReq.request_icon || sReq.request_icon) : (sReq ? sReq.request_icon : null),
          course_icon_slot: primaryCourseReq ? primaryCourseReq.course_icon_slot : null,
          request_color: primaryCourseReq ? (primaryCourseReq.color || sReq.color) : (sReq ? sReq.color : null),
          request_is_highlighted: highlightedRequests.findIndex(highlighted_course_id => `${highlighted_course_id}` === `${primaryCourseReq ? primaryCourseReq.course_id : null}`) !== -1,
          is_deleted: primaryCourseReq ? (primaryCourseReq.is_deleted === '1') : false,
          is_inclusion: primaryCourseReq ? requestIsInclusion(primaryCourseReq) : false
        };
      }),
    [studentRequests, coursesIndex, highlightedRequests, courseIconsByCourseID]
  );

  const addStudentRequest = async (course) => {
    const tempGroupId = `NEW-${Date.now()}`;
    const courseIcon = getCourseIcon(courseIconsByCourseID, course.course_id);
    const newCourseRequest = {
      id: tempGroupId,
      name: `${course.name} (${course.course_code})`,
      credits: course.credits,
      priority: "1",
      course_requests: [{
        course_id: course.course_id,
        name: `${course.name} (${course.course_code})`,
        credits: course.credits,
        order: "1",
        is_inclusion: "0",
        course_icon_slot: courseIcon.icon_slot,
        color: courseIcon.color,
      }],
      request_course_id: course.course_id,
      course_icon_slot: courseIcon.icon_slot,
      request_color: courseIcon.color,
      isOpen: false,
    };

    const validation = await validateRequest([...studentRequestsState, newCourseRequest], 'add-request', null);
    if (validation.conflictType === 'new_primary_remove_alternate') {

      // find group id where theres an alternate course_id  that maches with course.course_id
      let targetGroupId = null;
      for (let req of studentRequestsState) {
        for (let alt of req.course_requests) {
          if (alt.course_id === course.course_id && alt.order !== "1") {
            targetGroupId = req.id;
            break;
          }
        }
        if (targetGroupId) break;
      }
      if (targetGroupId) {
        removeAlternate(course.course_id, targetGroupId);
      }
    } else if (!validation.valid) {
      return false;
    }

    const updatedRequests = [...studentRequestsState, newCourseRequest];
    setStudentRequestsState(updatedRequests);
    onUpdateStudentRequests(updatedRequests);
    onUpdateData('add', tempGroupId, 'student_requests', course.course_id);
    return true;
  }

  const removeStudentRequest = (groupId) => {
    const updatedRequests = studentRequestsState.filter((req) => req.id !== groupId);
    setStudentRequestsState(updatedRequests);
    onUpdateStudentRequests(updatedRequests);
    onUpdateData('remove', groupId, 'student_requests');
  };

  const changeStudentRequestPriority = async (groupId, newPriority) => {
    const newStudentRequestsState = studentRequestsState.map((req) => {
      if (req.id === groupId) {
        return {
          ...req,
          priority: String(newPriority),
        };
      }
      return req;
    });

    const validation = await validateRequest(newStudentRequestsState, 'change-priority', groupId);
    if (!validation.valid) return;

    setStudentRequestsState(newStudentRequestsState);
    onUpdateStudentRequests(newStudentRequestsState);
    onUpdateData('change', groupId, 'student_requests', String(newPriority), false, 'priority');
  };

  const changeCourseRequestInclusion = (groupId, courseId, isInclusion) => {
    let updatedCourseRequests = [];
    const inclusionValue = isInclusion ? "1" : "0";
    const updatedRequests = studentRequestsState.map((requestGroup) => {
      if (`${requestGroup.id}` !== `${groupId}`) return requestGroup;

      updatedCourseRequests = requestGroup.course_requests.map((courseRequest) => (
        `${courseRequest.course_id}` === `${courseId}`
          ? { ...courseRequest, is_inclusion: inclusionValue }
          : courseRequest
      ));
      const primaryRequest = updatedCourseRequests.find((courseRequest) => `${courseRequest.order}` === "1");

      return {
        ...requestGroup,
        course_requests: updatedCourseRequests,
        is_inclusion: primaryRequest ? requestIsInclusion(primaryRequest) : requestGroup.is_inclusion,
      };
    });

    if(updatedCourseRequests.length === 0) return;
    setStudentRequestsState(updatedRequests);
    onUpdateStudentRequests(updatedRequests);
    onUpdateData('change', groupId, 'course_requests', updatedCourseRequests);
  };

  const removeAlternate = (alternateId, studentReqId) => {
    let updatedAlternates = [];
    const updatedRequests = studentRequestsState.map((req) => {
      if (req.id === studentReqId) {
        const primary = req.course_requests.filter((alt) => alt.order === "1")[0];
        const newAlternates = req.course_requests
          .filter((alt) => (alt.course_id !== alternateId && alt.course_id !== primary.course_id))
          .map((alt, index) => ({
            ...alt,
            order: (index + 2).toString() // Start from 2 since 1 is primary
          }));

        updatedAlternates = [
          primary, // primary course
          ...newAlternates
        ];

        return {
          ...req,
          course_requests: updatedAlternates
        };
      }
      return req;
    })
    setStudentRequestsState(updatedRequests);
    onUpdateStudentRequests(updatedRequests);
    onUpdateData('change', studentReqId, 'course_requests', updatedAlternates);
  };

  const moveAlternate = useCallback((fromIndex, toIndex, studentReqId) => {
    let updatedAlternates = [];
    const updatedRequests = studentRequestsState.map((req) => {
      if (req.id === studentReqId) {
        const primary = req.course_requests.filter((alt) => alt.order === "1")[0];
        const newAlternates = [...req.course_requests];
        const [movedItem] = newAlternates.splice(fromIndex + 1, 1);
        newAlternates.splice(toIndex + 1, 0, movedItem);
        // Update order for all items
        updatedAlternates = newAlternates
          .filter(alt => alt.order !== "1")
          .map((alt, index) => ({
            ...alt,
            order: (index + 2).toString() // Start from 2 since 1 is primary
          }));
        updatedAlternates = [
          primary, // primary course
          ...updatedAlternates
        ];

        return {
          ...req,
          course_requests: updatedAlternates,
        };
      }
      return req;
    })
    setStudentRequestsState(updatedRequests);  
    onUpdateStudentRequests(updatedRequests);
    onUpdateData('change', studentReqId, 'course_requests', updatedAlternates);
  }, [onUpdateData, onUpdateStudentRequests, studentRequestsState]);

  const toggleOpenAlternates = (courseId) => {
    setStudentRequestsState((prevRequests) =>
      prevRequests.map((req) => {
        if (req.id === courseId) {
          return {
            ...req,
            isOpen: !req.isOpen,
          };
        }
        return req;
      })
    );
  };

  const addAlternate = async (course, groupId) => {
    const courseIcon = getCourseIcon(courseIconsByCourseID, course.course_id);
    const newCourse = {
      course_id: course.course_id,
      name: course.name,
      credits: course.credits,
      is_inclusion: "0",
      course_icon_slot: courseIcon.icon_slot,
      color: courseIcon.color,
    };

    let order = 1;
    const newStudentRequestsState = studentRequestsState.map((req) => {
      if (req.id === groupId) {
        order = String(req.course_requests.length + 1); // +1 for the primary course
        return {
          ...req,
          course_requests: [
            ...req.course_requests,
            {
              ...newCourse,
              order,
            },
          ],
        };
      }
      return req;
    });

    const validation = await validateRequest(newStudentRequestsState, 'add-alternate', groupId);
    if (!validation.valid) return false;

    setStudentRequestsState(newStudentRequestsState);
    onUpdateStudentRequests(newStudentRequestsState);
    onUpdateData('add', groupId, 'course_requests', course.course_id, false, null, { order });
    return true;
  };

  const setAddAlternateOpen = (groupId) => {
    if (openedAlternate.includes(groupId)) {
      setOpenedAlternate((prev) => prev.filter((id) => id !== groupId));
    } else {
      setOpenedAlternate((prev) => [...prev, groupId]);
    }
  }

  const validateRequest = async (studentRequestsState, context = null, targetId = null) => {
    setValidateRequestsLoading({
      isLoading: true,
      context: context,
      targetId: targetId
    });

    const validationResponse = {
      valid: false,
      conflictType: null
    };
    
    try {
      const validation = await studentValidateRequests(
        schoolId,
        scheduleVersionId,
        studentRequestsState,
      );
      setValidateRequestsLoading({
        isLoading: false,
        context: null,
        targetId: null
      });
      
      if (validation.valid) {
        validationResponse.valid = true;
        return validationResponse;
      }

      if (!validation.valid && validation.conflict) {
        const result = await swal({
          title: "Validation Conflicts Detected",
          text: `The following conflicts were found:\n\n${validation.conflict}\n\n`,
          icon: "warning",
          buttons: {
            // cancel: {
            //   text: "No",
            //   value: false,
            //   visible: true,
            //   className: "swal-button--cancel"
            // },
            confirm: {
              text: "OK",
              value: false,
              visible: true,
              className: "swal-button--confirm"
            }
          },
          dangerMode: true,
        });
        validationResponse.valid = result;
        validationResponse.conflictType = validation.conflictType ? validation.conflictType : null;
        return validationResponse;
      }

      // Fallback
      return validationResponse;

    } catch (error) {
      setValidateRequestsLoading({
        isLoading: false,
        context: null,
        targetId: null
      });
      await swal({
        title: "Validation Error",
        text: "An error occurred while validating requests. Please try again.",
        icon: "error",
        button: "OK"
      });
      
      return validationResponse;
    }
  }

  useEffect(() => {
    setStudentRequestsState((previousRequests) => {
      return initialCourseRequests.map((request) => {
        const previousRequest = previousRequests.find((previous) => previous.id === request.id);
        return {
          ...request,
          isOpen: previousRequest ? previousRequest.isOpen : request.isOpen,
        };
      });
    });
  }, [initialCourseRequests]);

  const fuzzySearch = useCallback(
    (e) => {
      const search_value = e.target.value;
      if (search_value.trim().length === 0) {
        setStudentRequestResults([...courses]);
        return;
      }

      const fuse = new Fuse(courses, {
        keys: ["name", "course_code"],
        threshold: 0.1,
      });
      const results = fuse.search(search_value);
      const course_results = results.map((result) => result.item);
      setStudentRequestResults(course_results);
    },
    [courses]
  );

  useEffect(() => {
    if (addStudentRequestOpen || openedAlternate.length > 0) {
      setStudentRequestResults([...courses]);
    }
  }, [addStudentRequestOpen, openedAlternate, courses]);

  const handleHoverOverHighlight = (e, hover_type, request_course_id) => {
    if(hover_type === 'mouse-in') {
      const request_info = studentRequestsState.find(request => request.request_course_id === request_course_id);
      const request_color = request_info?.request_color;
      
      if(request_color) {
        e.currentTarget.classList.add(request_color);
      }
    }
    else if(hover_type === 'mouse-out') {
      e.currentTarget.className = 'request-icon';
    }
  };

  const credits = useMemo(() => studentRequestsState.reduce((total, req) => {
    return total + (Number(req.credits) || 0);
  }, 0), [studentRequestsState]);

  const shouldShowMatchState = studentCourseSections && studentCourseSections.length > 0;
  const shouldMatchInclusionState = `${scheduleInfo?.separate_ICR_codes}` === '0';
  const shouldShowInclusionToggle = editable && shouldMatchInclusionState;

  const courseRequestIsMatched = useCallback((courseID, isInclusion = false) => {
    if (!shouldShowMatchState || !courseID) return null;
    const requestMatchesInclusion = isInclusion === true || `${isInclusion}` === '1';

    return studentCourseSections.findIndex((studentSection) => {
      const sameCourse = `${studentSection.course_id}` === `${courseID}`;
      if (!sameCourse || !Array.isArray(studentSection.course_periods) || studentSection.course_periods.length === 0) return false;
      if(shouldMatchInclusionState && (`${studentSection.is_inclusion || '0'}` === '1') !== requestMatchesInclusion) return false;

      return sectionSatisfiesStudentLabelsForCourse({
        section_label_ids: studentSection.labels || [],
        student_labels: studentLabels,
        course_id: courseID,
        course_section_label_map: courseSectionLabelMap
      });
    }) !== -1;
  }, [shouldShowMatchState, studentCourseSections, studentLabels, courseSectionLabelMap, shouldMatchInclusionState]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="course-request-list">

        {showHeader && <div className="course-request-header">
          <div>Course</div>
          <div>Priority</div>
          <div></div>
        </div>}
        {studentRequestsState.map((studentReq) => {

          const matched = courseRequestIsMatched(studentReq.request_course_id, studentReq.is_inclusion);
          const anyMatched = (!shouldShowMatchState)
            ? null
            : studentReq.course_requests.some((requestCourse) => courseRequestIsMatched(requestCourse.course_id, requestIsInclusion(requestCourse)));
          const requestLabels = getRequestLabels(studentReq.request_course_id);
          const showRequestChips = requestLabels.length > 0 || studentReq.is_inclusion;

          return <React.Fragment key={studentReq.id}>
            <div className={`course-card ${anyMatched === true ? 'matched' : (anyMatched === false ? 'unmatched' : '')}`}>
              <div className="course-card-header" onClick={() => onRequestRowClick(studentReq)}>
                <div className="course-header-left">
                  {(studentReq.course_icon_slot !== null || studentReq.request_icon) &&
                    <div
                      className={`request-icon ${studentReq.is_deleted && 'disabled-row'} ${studentReq.request_is_highlighted && studentReq.request_color} `}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRequestHighlight(studentReq.request_course_id);
                      }}
                      onMouseEnter={!studentReq.request_is_highlighted ? (e) => handleHoverOverHighlight(e, 'mouse-in', studentReq.request_course_id) : ()=>{}}
                      onMouseLeave={!studentReq.request_is_highlighted ? (e) => handleHoverOverHighlight(e, 'mouse-out', studentReq.request_course_id):()=>{}}>
                        <CourseIcon iconSlot={studentReq.course_icon_slot} legacyIconKey={studentReq.request_icon} />
                    </div>
                  }
                  <div className="course-title-with-labels">
                    <span className="course-title">{studentReq.name}</span>
                    {showRequestChips &&
                      <div className="course-request-label-row">
                        {studentReq.is_inclusion &&
                          <div className="course-request-label-chip orange-text" key={`${studentReq.id}-inclusion`}>Inclusion</div>
                        }
                        {requestLabels.map((labelInfo) => (
                          <div className={`course-request-label-chip ${getLabelTextColorClass(labelInfo)}`} key={`${studentReq.id}-${labelInfo.label_id}`}>{labelInfo.label}</div>
                        ))}
                      </div>
                    }
                  </div>
                  {editable && <button
                    type="button"
                    className={
                      "alternates-toggle" +
                      ((studentReq.course_requests.length - 1) === 0
                        ? " alternates-empty"
                        : "")
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onCloseRequestExpandedContent();
                      toggleOpenAlternates(studentReq.id);
                    }}
                  >
                    {(studentReq.course_requests.length - 1)} Alternates
                  </button>}
                </div>

                <div className="course-header-center">
                  {editable && <>
                    <select
                      className="quantity-select"
                      value={studentReq.priority || 1}
                      disabled={validateRequestsLoading.isLoading && validateRequestsLoading.context === 'change-priority' && validateRequestsLoading.targetId === studentReq.id}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) =>
                        changeStudentRequestPriority(
                          studentReq.id,
                          parseInt(event.target.value)
                        )
                      }
                    >
                      {Array.from({ length: studentRequestsState.length }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}
                        </option>
                      ))}
                    </select>
                    {validateRequestsLoading.isLoading && validateRequestsLoading.context === 'change-priority' && validateRequestsLoading.targetId === studentReq.id && (
                      <div style={{ position: 'absolute', marginLeft: '15px', height: '30px' }}>
                        <Loader size="32px" />
                      </div>
                    )}
                  </>}
                  {!editable && <button
                    type="button"
                    className={
                      "alternates-toggle" +
                      ((studentReq.course_requests.length - 1) === 0
                        ? " alternates-empty"
                        : "")
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onCloseRequestExpandedContent();
                      toggleOpenAlternates(studentReq.id);
                    }}
                  >
                    {(studentReq.course_requests.length - 1)} Alternates
                  </button>}
                </div>

                <div className="course-header-right">
                  {shouldShowInclusionToggle &&
                    <div className="course-request-inclusion-toggle" onClick={(event) => event.stopPropagation()}>
                      <span>Inclusion</span>
                      <Toggle
                        toggleOn={studentReq.is_inclusion}
                        handleChange={(nextState) => changeCourseRequestInclusion(studentReq.id, studentReq.request_course_id, nextState)}
                        isSmall={true}
                        toggleColor="yellow"
                      />
                    </div>
                  }
                  {matched ? <span className="matched-label">Matched</span> : <span className="unmatched-label">Unmatched</span>}
                  <span className={`course-credits ${matched === true ? 'green-text' : (matched === false ? 'gray-text' : '')}`}>
                    {studentReq.credits} credits
                  </span>
                  {editable && <button
                  className="course-remove gray-to-red-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCloseRequestExpandedContent();
                    removeStudentRequest(studentReq.id);
                  }}
                >
                    <FontAwesomeIcon
                      className="course-remove-icon"
                      icon={faTrashAlt}
                    />
                  </button>}
                </div>
              </div>
              {studentReq.isOpen && (
                <div className="alternates-container">
                  <div className="alternates-panel">
                    {studentReq.course_requests
                      .filter((alt) => alt.order !== "1")
                      .map((alt, index) => (
                      <DraggableAlternateRow
                        key={alt.course_id}
                        alt={alt}
                        index={index}
                        studentReqId={studentReq.id}
                        moveAlternate={moveAlternate}
                        removeAlternate={removeAlternate}
                        editable={editable}
                        isCourseMatched={courseRequestIsMatched}
                        requestLabels={
                          getRequestLabels(alt.course_id)
                        }
                        requestIcon={alt.request_icon}
                        requestIconSlot={alt.course_icon_slot}
                        requestColor={alt.color}
                        requestIsHighlighted={highlightedRequests.findIndex(highlightedCourseId => `${highlightedCourseId}` === `${alt.course_id}`) !== -1}
                        toggleRequestHighlight={toggleRequestHighlight}
                        showInclusionToggle={shouldShowInclusionToggle}
                        changeCourseRequestInclusion={changeCourseRequestInclusion}
                      />
                    ))}

                    {(!openedAlternate.includes(studentReq.id) && editable) && (
                      <div className="add-course-row">
                        <button
                          className="add-course-button"
                          type="button"
                          disabled={validateRequestsLoading.isLoading && validateRequestsLoading.context === 'add-alternate' && validateRequestsLoading.targetId === studentReq.id}
                          onClick={() => setAddAlternateOpen(studentReq.id)}
                        >
                          <FontAwesomeIcon icon={faPlusCircle} />
                          Add Alternate
                        </button>
                        {validateRequestsLoading.isLoading && validateRequestsLoading.context === 'add-alternate' && validateRequestsLoading.targetId === studentReq.id && (
                          <div style={{ position: 'absolute', marginLeft: '140px', height: '32px' }}>
                            <Loader size="32px" />
                          </div>
                        )}
                      </div>
                    )}

                    <SearchAddCourse
                      isOpen={openedAlternate.includes(studentReq.id)}
                      onClose={() => setAddAlternateOpen(studentReq.id)}
                      searchResults={studentRequestResults}
                      onSearch={fuzzySearch}
                      onAdd={(course) => addAlternate(course, studentReq.id)}
                      isSelected={(course) => studentReq.course_requests.some(request => request.course_id === course.course_id)}
                    />
                  </div>
                </div>
              )}
            </div>
            {renderRequestExpandedContent && renderRequestExpandedContent(studentReq)}
          </React.Fragment>
        })}
      </div>
      
      {studentRequestsState.length === 0 && (
        <div className='edit-data-no-data-container'>No courses have been requested yet!</div>
      )}
      <div className="edit-student-requests-row">
        {editable && <>
          <div
            className="edit-data-add-new-btn blue-link"
            style={{
              opacity: validateRequestsLoading.isLoading && validateRequestsLoading.context === 'add-request' ? 0.5 : 1,
              pointerEvents: validateRequestsLoading.isLoading && validateRequestsLoading.context === 'add-request' ? 'none' : 'auto'
            }}
            onClick={() => setAddStudentRequestOpen(true)}
          >
            <FontAwesomeIcon icon={faPlusCircle} />
            <div>Add Course Request</div>
            {validateRequestsLoading.isLoading && validateRequestsLoading.context === 'add-request' && (
              <div style={{ position: 'absolute', marginLeft: '180px', height: '32px' }}>
                <Loader size="32px" />
              </div>
            )}
          </div>
        </>}
        {showTotalCredits && <div className="edit-data-student-total-credits">
          Total Credits: {credits}
        </div>}
      </div>

      <SearchAddCourse
        isOpen={addStudentRequestOpen}
        onClose={() => setAddStudentRequestOpen(false)}
        searchResults={studentRequestResults}
        onSearch={fuzzySearch}
        onAdd={addStudentRequest}
        isSelected={(course) => studentRequestsState.some(requestGroup => requestGroup.course_requests.some(request => request.course_id === course.course_id))}
      />
    </DndProvider>
  );
}

const DraggableAlternateRow = ({ 
  alt, 
  index, 
  studentReqId, 
  moveAlternate, 
  removeAlternate,
  editable,
  isCourseMatched = () => null,
  requestLabels = [],
  requestIcon = null,
  requestIconSlot = null,
  requestColor = null,
  requestIsHighlighted = false,
  toggleRequestHighlight = () => {},
  showInclusionToggle = false,
  changeCourseRequestInclusion = () => {}
}) => {
  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: 'ALTERNATE',
    item: { id: alt.course_id, index, studentReqId },
    hover: (draggedItem) => {
      if (draggedItem.studentReqId !== studentReqId) return;
      if (draggedItem.index !== index) {
        moveAlternate(draggedItem.index, index, studentReqId);
        draggedItem.index = index;
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'ALTERNATE',
    hover: (draggedItem) => {
      if (draggedItem.studentReqId !== studentReqId) return;
      if (draggedItem.index !== index) {
        moveAlternate(draggedItem.index, index, studentReqId);
        draggedItem.index = index;
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const matched = isCourseMatched(alt.course_id, requestIsInclusion(alt));
  const showRequestChips = requestLabels.length > 0 || requestIsInclusion(alt);
  const handleIconHover = (e, hoverType) => {
    if(!requestColor) return;
    if(hoverType === 'mouse-in') e.currentTarget.classList.add(requestColor);
    else e.currentTarget.className = 'request-icon';
  };

  return (
    <div 
      ref={(node) => dragPreview(drop(node))}
      className={`alternate-row unmatched ${isDragging ? 'dragging' : ''} ${isOver ? 'drop-hover' : ''}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      
      <div className="alternate-center">
        <div className="alternate-center-left">
          {editable && <div className="alternate-drag" ref={drag}>
            <FontAwesomeIcon
              className="drag-handle"
              icon={faBars}
            />  
          </div>}
          {(requestIconSlot !== null || requestIcon) &&
            <div
              className={`request-icon ${requestIsHighlighted && requestColor} `}
              onClick={(e) => {
                e.stopPropagation();
                toggleRequestHighlight(alt.course_id);
              }}
              onMouseEnter={!requestIsHighlighted ? (e) => handleIconHover(e, 'mouse-in') : ()=>{}}
              onMouseLeave={!requestIsHighlighted ? (e) => handleIconHover(e, 'mouse-out') : ()=>{}}
            >
              <CourseIcon iconSlot={requestIconSlot} legacyIconKey={requestIcon} />
            </div>
          }
          <div className="alternate-center-left-text" style={!editable ? { marginLeft: '15px' } : {}}>
            <div className="alternate-title-with-labels">
              <span className="alternate-name">{alt.name}</span>
              {showRequestChips &&
                <div className="course-request-label-row">
                  {requestIsInclusion(alt) &&
                    <div className="course-request-label-chip orange-text" key={`${studentReqId}-${alt.course_id}-inclusion`}>Inclusion</div>
                  }
                  {requestLabels.map((labelInfo) => (
                    <div className={`course-request-label-chip ${getLabelTextColorClass(labelInfo)}`} key={`${studentReqId}-${alt.course_id}-${labelInfo.label_id}`}>{labelInfo.label}</div>
                  ))}
                </div>
              }
            </div>
            <span className="alternate-pill">Alternate</span>
          </div>
        </div>
        <div className="alternate-center-center">
          <span className="alternate-index">{alt.order - 1}</span>
        </div>
        <div className="alternate-center-right">
          {showInclusionToggle &&
            <div className="course-request-inclusion-toggle">
              <span>Inclusion</span>
              <Toggle
                toggleOn={requestIsInclusion(alt)}
                handleChange={(nextState) => changeCourseRequestInclusion(studentReqId, alt.course_id, nextState)}
                isSmall={true}
                toggleColor="yellow"
              />
            </div>
          }
          {matched ? <span className="matched-label">Matched</span> : <span className="unmatched-label">Unmatched</span>}
          <span className={`alternate-credits ${matched === true ? 'green-text' : (matched === false ? 'gray-text' : '')}`}>
            {alt.credits} credits
          </span>

          {editable && <FontAwesomeIcon
            className="alternate-trash gray-to-red-link"
            icon={faTimes}
            onClick={() => removeAlternate(alt.course_id, studentReqId)}
          />}
        </div>
      </div>
    </div>
  );
};

const SearchAddCourse = ({
  isOpen,
  onClose,
  searchResults,
  onSearch,
  onAdd,
  isSelected = () => false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="search-add-row-container">
      <div className="search-add-search-container">
        <div className="search-add-adding-input-container">
          <input
            id="search-student-request-input"
            className="search-add-adding-input"
            onChange={onSearch}
            placeholder="Search for course"
          />
          <FontAwesomeIcon
            className="search-add-adding-input-icon"
            icon={faSearch}
          />
        </div>
        <FontAwesomeIcon
          className="x-cancel search-add-cancel-new-btn"
          icon={faTimes}
          onClick={onClose}
        />
      </div>
      {searchResults.length !== 0 && (
        <div className="search-add-search-results">
          <div className="search-add-did-you-mean">Did you mean:</div>
          {searchResults.map((course, i) => {
            const courseSelected = isSelected(course);
            return (
              <div className="search-add-search-result" key={i}>
                <div>
                  {course.name} ({course.course_code})
                </div>
                <div>
                  <SearchAddButton selected={courseSelected} onAdd={() => onAdd(course)} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
